import { t } from "@lingui/macro";
import { ArrowDown, ArrowsVertical, DotsSixVertical } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

const STORAGE_KEY = "onboarding-left-sidebar-seen";

type HintProps = {
  icon: React.ReactNode;
  text: string;
  delay?: number;
};

const Hint = ({ icon, text, delay = 0 }: HintProps) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0, transition: { delay } }}
    exit={{ opacity: 0, y: -8 }}
    className="flex items-start gap-x-2.5 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2.5"
  >
    <div className="mt-0.5 shrink-0 text-primary">{icon}</div>
    <p className="text-muted-foreground text-xs leading-relaxed">{text}</p>
  </motion.div>
);

export const OnboardingHints = () => {
  const [searchParams] = useSearchParams();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (searchParams.get("onboarding") === "1") return;
    const seen = localStorage.getItem(STORAGE_KEY);
    if (!seen) setVisible(true);
  }, [searchParams]);

  const dismiss = useCallback(() => {
    setVisible(false);
    localStorage.setItem(STORAGE_KEY, "true");
  }, []);

  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(dismiss, 30000);
    return () => clearTimeout(timer);
  }, [visible, dismiss]);

  if (!visible) return null;

  return (
    <AnimatePresence>
      <div className="grid gap-y-2">
        <Hint
          icon={<ArrowDown size={14} weight="bold" />}
          text={t`Scroll down to see all your resume sections`}
          delay={0.1}
        />
        <Hint
          icon={<DotsSixVertical size={14} weight="bold" />}
          text={t`Drag sections to reorder them in your resume`}
          delay={0.3}
        />
        <Hint
          icon={<ArrowsVertical size={14} weight="bold" />}
          text={t`Click Main or Side to move sections between columns`}
          delay={0.5}
        />

        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { delay: 0.7 } }}
          onClick={dismiss}
          className="text-muted-foreground/60 hover:text-muted-foreground mt-1 text-center text-xs transition-colors"
        >
          {t`Dismiss`}
        </motion.button>
      </div>
    </AnimatePresence>
  );
};
